import { createContext, useContext, useEffect } from 'react';

/** What a screen with editable state tells the layout around it. */
export interface UnsavedGuard {
  dirty: boolean;
  /** Persist the work; resolves true when it was saved and leaving is safe. */
  save: () => Promise<boolean>;
}

interface UnsavedGuardContextValue {
  /** Register (or clear, with null) the current screen's guard. */
  setGuard: (guard: UnsavedGuard | null) => void;
  /** Run `go` now, or after the user resolves the "unsaved changes" alert. */
  attempt: (go: () => void) => void;
}

const UnsavedGuardContext = createContext<UnsavedGuardContextValue>({
  setGuard: () => {},
  attempt: (go) => go(),
});

export const UnsavedGuardProvider = UnsavedGuardContext.Provider;

/** Wrap any in-app navigation that leaves the screen. Outside a provider it just runs. */
export function useGuardedNavigation(): (go: () => void) => void {
  return useContext(UnsavedGuardContext).attempt;
}

/** Called by an editor on every render with its latest dirty flag and save. */
export function useUnsavedGuard(dirty: boolean, save: () => Promise<boolean>) {
  const { setGuard } = useContext(UnsavedGuardContext);
  useEffect(() => {
    setGuard({ dirty, save });
  });
  useEffect(() => () => setGuard(null), [setGuard]);
}
